import { getApiHosts, getDefaultHeaders, spaHeaderOptions, type Env } from '@fff-web/constants';
import { createRequest } from '@fff-web/request';
import type {
  BffClientInfo,
  GetCancellationContextError,
  GetCancellationContextResponse,
  GetChatbotContextError,
  GetChatbotContextParams,
  GetChatbotContextResponse,
  GetCustomizeAccessError,
  GetCustomizeAccessParams,
  GetCustomizeAccessResponse,
  GetCustomizeAccessV2Error,
  GetCustomizeAccessV2Params,
  GetCustomizeAccessV2Response,
  GetLiveSalesError,
  GetLiveSalesParams,
  GetLiveSalesResponse,
  GetMenuDataError,
  GetMenuDataResponse,
  GetNavbarProfileError,
  GetNavbarProfileParams,
  GetNavbarProfileResponse,
  GetPublicCustomizeAccessV2Error,
  GetPublicCustomizeAccessV2Params,
  GetPublicCustomizeAccessV2Query,
  GetPublicCustomizeAccessV2Response,
  GetSaleProductsBody,
  GetSaleProductsError,
  GetSaleProductsParams,
  GetSaleProductsResponse,
  InvalidateMenuDataError,
  InvalidateMenuDataResponse,
  InvalidateNavbarProfileError,
  InvalidateNavbarProfileResponse,
} from './types';
import { toGetCustomizeAccessV2Query, toGetLiveSalesQuery } from './wire';

type NavbarProfileContextHeaders = {
  [headerName: string]: string;
};

/**
 * Asado SDK.
 * @description
 * Asado SDK is a simple function that takes the environment as parameter (`env`) and
 * returns a set of functions to interact with the asado API. The url used
 * will depend on the environment passed when initializing the SDK.
 *
 * The functions from this SDK are meant to be used on any environment, so
 * the redirect and credentials properties are set to the default fetch values.
 * This is because API calls in Maui will always hit the NextJS API routes,
 * which will handle the redirection. This also means that credentials will
 * be sent only on same-origin requests.
 *
 * If you want to use this in the client, you can initialize
 * the SDK with the parameter `spa = true` to set the redirect policy to
 * manual and credentials to be always included. Please remember that this
 * is not recommended as it has potential security implications. In the future
 * this option will be removed.
 *
 * For more information about Asado APIs please check Swagger:
 * @see {link} https://asado.fff-dev.com/docs
 */
export function asadoSdk(env: Env, spa = false, host?: string, client?: BffClientInfo) {
  if (!env) {
    throw new Error('Environment is required.');
  }

  const options = spa ? spaHeaderOptions : {};
  if (!host) {
    const { ASADO_API_BASE_HOST } = getApiHosts(env, ['ASADO_API_BASE_HOST']);
    host = `${ASADO_API_BASE_HOST}`;
  }
  const defaultHeaders = {
    ...getDefaultHeaders(env, true),
    ...(client?.platform && { 'x-client-platform': client.platform }),
    ...(client?.version && { 'x-client-version': client.version }),
  };
  const bffPath = (version: 'v1' | 'v2') => `${host}/bff/${version}`;

  return {
    getCustomizeAccess: (params: GetCustomizeAccessParams, jwtToken?: string) =>
      createRequest<GetCustomizeAccessParams, GetCustomizeAccessResponse, GetCustomizeAccessError>({
        url: `${bffPath('v1')}/customize/access`,
        method: 'GET',
        fetchOptions: {
          headers: { ...defaultHeaders, ...(jwtToken && { Cookie: `Jwt=${jwtToken}` }) },
          ...options,
        },
      })(params),

    getCustomizeAccessV2: (params: GetCustomizeAccessV2Params, jwtToken?: string) =>
      createRequest<
        ReturnType<typeof toGetCustomizeAccessV2Query>,
        GetCustomizeAccessV2Response,
        GetCustomizeAccessV2Error
      >({
        url: `${bffPath('v2')}/customize/access`,
        method: 'GET',
        fetchOptions: {
          headers: { ...defaultHeaders, ...(jwtToken && { Cookie: `Jwt=${jwtToken}` }) },
          ...options,
        },
      })(toGetCustomizeAccessV2Query(params)),

    getPublicCustomizeAccessV2: ({
      forwardedHeaders,
      ...params
    }: GetPublicCustomizeAccessV2Params) =>
      createRequest<
        GetPublicCustomizeAccessV2Query,
        GetPublicCustomizeAccessV2Response,
        GetPublicCustomizeAccessV2Error
      >({
        url: `${bffPath('v2')}/public/customize/access`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(params),

    getNavbarProfile: (
      contextHeaders?: NavbarProfileContextHeaders,
      params?: GetNavbarProfileParams,
    ) =>
      createRequest<
        GetNavbarProfileParams | undefined,
        GetNavbarProfileResponse,
        GetNavbarProfileError
      >({
        url: `${bffPath('v1')}/navbar/profile`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(contextHeaders ?? {}),
          },
          ...options,
        },
      })(params),

    invalidateNavbarProfile: (contextHeaders?: NavbarProfileContextHeaders) => {
      // This DELETE has no request body. `defaultHeaders` sets
      // `Content-Type: application/json`, which makes Fastify's JSON body parser
      // reject the empty body with `FST_ERR_CTP_EMPTY_JSON_BODY` (400). Drop the
      // Content-Type so the body-less request is accepted (it's harmless on the
      // GET above because Fastify never body-parses GETs).
      const { 'Content-Type': _contentType, ...headersWithoutContentType } =
        defaultHeaders as Record<string, string>;
      return createRequest<
        undefined,
        InvalidateNavbarProfileResponse,
        InvalidateNavbarProfileError
      >({
        url: `${bffPath('v1')}/navbar/profile/cache`,
        method: 'DELETE',
        fetchOptions: {
          headers: {
            ...headersWithoutContentType,
            ...(contextHeaders ?? {}),
          },
          ...options,
        },
      })(undefined);
    },

    invalidateMenuData: (contextHeaders?: NavbarProfileContextHeaders) => {
      // Body-less DELETE, same handling as `invalidateNavbarProfile`: drop Content-Type so
      // Fastify's JSON body parser doesn't reject the empty body (`FST_ERR_CTP_EMPTY_JSON_BODY`).
      const { 'Content-Type': _contentType, ...headersWithoutContentType } =
        defaultHeaders as Record<string, string>;
      return createRequest<undefined, InvalidateMenuDataResponse, InvalidateMenuDataError>({
        url: `${bffPath('v1')}/user/menu-data/cache`,
        method: 'DELETE',
        fetchOptions: {
          headers: {
            ...headersWithoutContentType,
            ...(contextHeaders ?? {}),
          },
          ...options,
        },
      })(undefined);
    },

    getSaleProducts: ({ saleUuid, forwardedHeaders, ...body }: GetSaleProductsParams) =>
      createRequest<GetSaleProductsBody, GetSaleProductsResponse, GetSaleProductsError>({
        url: `${bffPath('v1')}/sales/${saleUuid}/products`,
        method: 'POST',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(body),

    getLiveSales: ({ preview, forwardedHeaders }: GetLiveSalesParams = {}) =>
      createRequest<
        ReturnType<typeof toGetLiveSalesQuery>,
        GetLiveSalesResponse,
        GetLiveSalesError
      >({
        url: `${bffPath('v1')}/sales/live`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(toGetLiveSalesQuery(preview)),

    getMenuData: (jwtToken?: string) =>
      createRequest<void, GetMenuDataResponse, GetMenuDataError>({
        url: `${bffPath('v1')}/user/menu-data`,
        method: 'GET',
        fetchOptions: {
          headers: { ...defaultHeaders, ...(jwtToken && { Cookie: `Jwt=${jwtToken}` }) },
          ...options,
        },
      })(),

    getCancellationContext: (jwtToken?: string) =>
      createRequest<void, GetCancellationContextResponse, GetCancellationContextError>({
        url: `${bffPath('v1')}/cancellation/context`,
        method: 'GET',
        fetchOptions: {
          headers: { ...defaultHeaders, ...(jwtToken && { Cookie: `Jwt=${jwtToken}` }) },
          ...options,
        },
      })(),

    getChatbotContext: ({ withMenuUserData, forwardedHeaders }: GetChatbotContextParams = {}) =>
      createRequest<
        { withMenuUserData?: boolean } | undefined,
        GetChatbotContextResponse,
        GetChatbotContextError
      >({
        url: `${bffPath('v1')}/chatbot/context`,
        method: 'GET',
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(forwardedHeaders ?? {}),
          },
          ...options,
        },
      })(withMenuUserData !== undefined ? { withMenuUserData } : undefined),
  };
}
